// Week-by-week league state derived from matchups alone, for the Ledger
// display concepts (standings journey, form, magic number, tale of the tape,
// playoff path). Pure functions; no React.
//
// Weeks after lastCompletedWeek are schedule only: the backend stores an
// unplayed game as 0-0 with no winner, which would otherwise read as a tie.
// Playoff and consolation games are excluded — these are regular-season views.

import { median } from './statistics.js';

const key = (id) => String(id);

function isScheduled(m) {
  return m.team1_id != null && m.team2_id != null && !m.is_playoffs && !m.is_consolation;
}

/** Regular-season games of one week (played or scheduled). */
export function regularSeasonGames(matchupsByWeek, week) {
  return (matchupsByWeek?.[week] ?? []).filter(isScheduled);
}

function outcomeFor(m, id) {
  if (m.winner_team_id == null) return 'T';
  return key(m.winner_team_id) === key(id) ? 'W' : 'L';
}

function allTeams(matchupsByWeek) {
  const teams = new Map();
  for (const games of Object.values(matchupsByWeek ?? {})) {
    for (const m of games ?? []) {
      if (m.team1_id != null && !teams.has(key(m.team1_id))) teams.set(key(m.team1_id), { teamId: m.team1_id, name: m.team1_name });
      if (m.team2_id != null && !teams.has(key(m.team2_id))) teams.set(key(m.team2_id), { teamId: m.team2_id, name: m.team2_name });
    }
  }
  return teams;
}

/**
 * Every team's record through `throughWeek`. Teams on a bye that week simply
 * gain no result. Ties count as a result 'T'.
 * @returns {Array<{teamId, name, wins, losses, ties, pointsFor, results: Array<{week, result, points, opponentId, opponentPoints}>}>}
 */
export function recordsThrough(matchupsByWeek, throughWeek) {
  const recs = new Map();
  for (const [k, t] of allTeams(matchupsByWeek)) {
    recs.set(k, { teamId: t.teamId, name: t.name, wins: 0, losses: 0, ties: 0, pointsFor: 0, results: [] });
  }
  for (let w = 1; w <= throughWeek; w++) {
    for (const m of regularSeasonGames(matchupsByWeek, w)) {
      const sides = [
        [m.team1_id, Number(m.team1_points) || 0, m.team2_id, Number(m.team2_points) || 0],
        [m.team2_id, Number(m.team2_points) || 0, m.team1_id, Number(m.team1_points) || 0],
      ];
      for (const [id, pts, oppId, oppPts] of sides) {
        const r = recs.get(key(id));
        const result = outcomeFor(m, id);
        r.results.push({ week: w, result, points: pts, opponentId: oppId, opponentPoints: oppPts });
        r.pointsFor += pts;
        if (result === 'W') r.wins++;
        else if (result === 'L') r.losses++;
        else r.ties++;
      }
    }
  }
  return [...recs.values()];
}

/** Wins with ties counted as half, the unit every comparison here uses. */
export function effectiveWins(r) {
  return r.wins + 0.5 * r.ties;
}

export function winPct(r) {
  const games = r.wins + r.losses + r.ties;
  return games ? effectiveWins(r) / games : 0;
}

/**
 * Orders records by win percentage, then points for, then name, and stamps a
 * 1-based `position`. Provider tiebreakers are not modelled.
 */
export function rankRecords(records) {
  return [...records]
    .sort((a, b) => winPct(b) - winPct(a) || b.pointsFor - a.pointsFor || String(a.name).localeCompare(String(b.name)))
    .map((r, i) => ({ ...r, position: i + 1 }));
}

/**
 * Position of every team after each completed week.
 *
 * `currentOrder` (team ids in the standings table's order) pins the final
 * week to the provider's standings, so the journey's last point always agrees
 * with the table beside it even where provider tiebreakers differ from ours.
 */
export function standingsJourney(matchupsByWeek, lastCompletedWeek, currentOrder = null) {
  const weeks = [];
  const positions = new Map();
  const pinned = currentOrder?.length ? new Map(currentOrder.map((id, i) => [key(id), i + 1])) : null;
  for (let w = 1; w <= lastCompletedWeek; w++) {
    weeks.push(w);
    for (const r of rankRecords(recordsThrough(matchupsByWeek, w))) {
      if (!positions.has(key(r.teamId))) positions.set(key(r.teamId), { teamId: r.teamId, name: r.name, positions: [] });
      const final = w === lastCompletedWeek && pinned?.has(key(r.teamId));
      positions.get(key(r.teamId)).positions.push(final ? pinned.get(key(r.teamId)) : r.position);
    }
  }
  return { weeks, teams: [...positions.values()] };
}

/** The last `n` results, oldest first, as 'W' | 'L' | 'T'. */
export function formOf(record, n = 5) {
  return record.results.slice(-n).map(x => x.result);
}

/**
 * Wins-or-rival-losses `me` still needs to finish above `chaser` (the first
 * team outside the playoff line), whatever else happens. Ties count half, and
 * the +1 means "outright" — a points tiebreak is not assumed. Null when it does
 * not apply: `me` is outside the line, no games remain, or nobody is below it.
 * The Overview (magicNumber) and Home both call this — one implementation.
 */
export function clinchMagic({ me, mePosition, chaser, spots, gamesRemaining }) {
  if (!me || !chaser || mePosition > spots || !(gamesRemaining > 0)) return null;
  return Math.max(0, Math.ceil(gamesRemaining + 1 - (effectiveWins(me) - effectiveWins(chaser))));
}

/** clinchMagic for a team in a ranked list (the Overview's standings). */
export function magicNumber({ ranked, teamId, spots, gamesRemaining }) {
  const me = ranked.find(r => key(r.teamId) === key(teamId));
  return clinchMagic({ me, mePosition: me?.position, chaser: ranked[spots], spots, gamesRemaining });
}

/** The team's regular-season game in `week`, or null on a bye / no game. */
export function gameFor(matchupsByWeek, teamId, week) {
  const m = regularSeasonGames(matchupsByWeek, week)
    .find(g => key(g.team1_id) === key(teamId) || key(g.team2_id) === key(teamId));
  if (!m) return null;
  const mine = key(m.team1_id) === key(teamId);
  return {
    week,
    points: Number(mine ? m.team1_points : m.team2_points) || 0,
    opponentId: mine ? m.team2_id : m.team1_id,
    opponentName: mine ? m.team2_name : m.team1_name,
    opponentPoints: Number(mine ? m.team2_points : m.team1_points) || 0,
    result: outcomeFor(m, teamId),
  };
}

/** Points recorded for every team in `week`, from the heatmap's per-team `weeks`. */
function weekPoints(heatmapTeams, week) {
  const points = [];
  for (const t of Object.values(heatmapTeams ?? {})) {
    const p = t?.weeks?.[week]?.points;
    if (p != null) points.push(p);
  }
  return points;
}

/**
 * Every team's all-play share of `week`: the fraction of that week's other
 * teams it out-scored (tied opponents count half), out of every OTHER team
 * with a recorded score that week — not the schedule's single opponent. A
 * team with no points that week (a bye) is left out of the map entirely, and
 * out of everyone else's denominator too.
 * @returns {Map<string, number>}
 */
function allPlayShares(heatmapTeams, week) {
  const scored = Object.entries(heatmapTeams ?? {})
    .map(([id, t]) => [key(id), t?.weeks?.[week]?.points])
    .filter(([, p]) => p != null);
  const shares = new Map();
  if (scored.length < 2) return shares; // nobody to compare against

  for (const [id, pts] of scored) {
    let beaten = 0;
    let tied = 0;
    for (const [otherId, otherPts] of scored) {
      if (otherId === id) continue;
      if (otherPts < pts) beaten++;
      else if (otherPts === pts) tied++;
    }
    shares.set(id, (beaten + 0.5 * tied) / (scored.length - 1));
  }
  return shares;
}

/**
 * Wins above (or below) expectation through `throughWeek`: actual effective
 * wins from the real schedule minus the all-play expected wins from weekly
 * points. Positive is lucky — winning more than the scores alone deserved.
 *
 * Matches the backend's `calculate_luck_index`, which `continue`s past a week
 * with no H2H result before touching either total: a week where the team had
 * no scheduled game (a bye) is excluded from `expected` here even though the
 * heatmap may still carry an all-play score that week — symmetric with
 * `actual`, which `recordsThrough` also never awards a result for on a bye. Without this, a bye deflates luck for every week from
 * the bye onward, breaking the "deltas between weeks are trustworthy"
 * guarantee.
 * @returns {Map<string, { actual: number, expected: number, luck: number }>}
 */
export function luckThrough(heatmapTeams, matchupsByWeek, throughWeek) {
  const result = new Map();
  for (const r of recordsThrough(matchupsByWeek, throughWeek)) {
    const k = key(r.teamId);
    const actual = effectiveWins(r);
    let expected = 0;
    for (let w = 1; w <= throughWeek; w++) {
      if (!gameFor(matchupsByWeek, r.teamId, w)) continue; // bye: no H2H result, exclude like the backend does
      const share = allPlayShares(heatmapTeams, w).get(k);
      if (share != null) expected += share;
    }
    result.set(k, { actual, expected, luck: actual - expected });
  }
  return result;
}

/**
 * One entry per week of the season for the season strip. Weeks beyond
 * `lastCompletedWeek` are schedule only (`played: false`): the opponent still
 * comes from `gameFor`, but points/result are not guessed at from the
 * backend's placeholder 0-0 row. `isTop`/`isBest`/`belowMedian` read the
 * heatmap's weekly points, independent of whether the team had a scheduled
 * game that week (a bye can still carry an all-play score).
 */
export function seasonStrip(heatmapTeams, matchupsByWeek, teamId, lastCompletedWeek, totalWeeks) {
  const tk = key(teamId);
  const entries = [];

  for (let w = 1; w <= totalWeeks; w++) {
    const played = w <= lastCompletedWeek;
    const points = heatmapTeams?.[tk]?.weeks?.[w]?.points ?? null;
    const game = gameFor(matchupsByWeek, teamId, w);

    const weekAll = weekPoints(heatmapTeams, w);
    const isTop = points != null && weekAll.length > 0 && points === Math.max(...weekAll);
    const belowMedian = points != null && weekAll.length > 0 && points < median(weekAll);

    const priorPoints = [];
    for (let pw = 1; pw < w; pw++) {
      const pp = heatmapTeams?.[tk]?.weeks?.[pw]?.points;
      if (pp != null) priorPoints.push(pp);
    }
    // Week 1 is never a PB (mock ledger-site.html line 552: `w > 1`) — with no
    // prior weeks to beat, `every()` on an empty array is vacuously true.
    const isBest = w > 1 && points != null && priorPoints.every(p => points > p);

    entries.push({
      week: w,
      played,
      points,
      result: played ? (game?.result ?? null) : null,
      opponentId: game?.opponentId ?? null,
      opponentName: game?.opponentName ?? null,
      isTop,
      isBest,
      belowMedian,
    });
  }
  return entries;
}

const MAX_PATH_GAMES = 16; // 65,536 outcomes — past that, enumeration is the wrong tool

/**
 * Every combination of the remaining regular-season games, grouped by the
 * viewer's own results. Only for the last one or two regular-season weeks —
 * earlier than that the grid would be 2^n cells of noise; the backend odds
 * simulation (phase 4) covers the rest of the season. Remaining games are
 * treated as equally likely and never tied. A tie on wins counts as 'tie'
 * (goes to the provider's tiebreak), never guessed.
 *
 * @returns {null | {
 *   weeks: Array<{ week, opponentId, opponentName } | { week, bye: true }>,
 *   cells: Record<string, { safe: number, tie: number, out: number, n: number }>,
 *   total: number, safeTotal: number,
 * }} keyed by the viewer's results in week order, e.g. 'WL'
 */
export function playoffPath({ matchupsByWeek, lastCompletedWeek, finalRegularWeek, teamId, spots }) {
  const remainingWeeks = [];
  for (let w = lastCompletedWeek + 1; w <= finalRegularWeek; w++) remainingWeeks.push(w);
  if (remainingWeeks.length < 1 || remainingWeeks.length > 2) return null;

  const base = new Map(recordsThrough(matchupsByWeek, lastCompletedWeek).map(r => [key(r.teamId), effectiveWins(r)]));
  if (!base.has(key(teamId)) || spots < 1 || spots >= base.size) return null;

  const games = remainingWeeks.flatMap(w => regularSeasonGames(matchupsByWeek, w).map(m => ({ week: w, a: key(m.team1_id), b: key(m.team2_id) })));
  if (games.length === 0 || games.length > MAX_PATH_GAMES) return null;

  const me = key(teamId);
  const cells = {};
  let safeTotal = 0;
  const total = 2 ** games.length;
  for (let mask = 0; mask < total; mask++) {
    const wins = new Map(base);
    let mine = '';
    games.forEach((g, i) => {
      const winner = (mask >> i) & 1 ? g.a : g.b;
      wins.set(winner, wins.get(winner) + 1);
      if (g.a === me || g.b === me) mine += winner === me ? 'W' : 'L';
    });
    const myWins = wins.get(me);
    let above = 0;
    let tied = 0;
    for (const [id, w] of wins) {
      if (id === me) continue;
      if (w > myWins) above++;
      else if (w === myWins) tied++;
    }
    const outcome = above + tied < spots ? 'safe' : above >= spots ? 'out' : 'tie';
    const cell = (cells[mine] ??= { safe: 0, tie: 0, out: 0, n: 0 });
    cell[outcome]++;
    cell.n++;
    if (outcome === 'safe') safeTotal++;
  }

  const weeks = remainingWeeks.map(w => {
    const g = gameFor(matchupsByWeek, teamId, w);
    return g ? { week: w, opponentId: g.opponentId, opponentName: g.opponentName } : { week: w, bye: true };
  });
  return { weeks, cells, total, safeTotal };
}

function pointsOf(m, id) {
  return Number(key(m.team1_id) === id ? m.team1_points : m.team2_points) || 0;
}

function opponentOf(m, id) {
  return key(key(m.team1_id) === id ? m.team2_id : m.team1_id);
}

/**
 * Each team's record over regular-season weeks 1..throughWeek had it played
 * another team's schedule. Row team `a` on column team `b`'s schedule plays
 * b's opponent that week, or b itself when that opponent is a. A pairing that
 * really happened (the diagonal, or a against b when they met) takes the
 * recorded result; any other pairing compares the two scores, equal scores
 * tying. A week where a or b had no game is skipped.
 * @param {string[]} teamIds
 * @returns {Map<string, Map<string, {wins: number, losses: number, ties: number}>>}
 */
export function scheduleLuck(matchupsByWeek, throughWeek, teamIds) {
  const ids = teamIds.map(key);
  const grid = new Map(ids.map(a => [a, new Map(ids.map(b => [b, { wins: 0, losses: 0, ties: 0 }]))]));
  for (let w = 1; w <= throughWeek; w++) {
    const gameOf = new Map();
    for (const m of regularSeasonGames(matchupsByWeek, w)) {
      gameOf.set(key(m.team1_id), m);
      gameOf.set(key(m.team2_id), m);
    }
    for (const a of ids) {
      const mine = gameOf.get(a);
      if (!mine) continue;
      for (const b of ids) {
        const theirs = gameOf.get(b);
        if (!theirs) continue;
        const opponent = opponentOf(theirs, b);
        let result;
        if (a === b || opponent === a) {
          result = outcomeFor(mine, a);
        } else {
          const diff = pointsOf(mine, a) - pointsOf(theirs, opponent);
          result = diff > 0 ? 'W' : diff < 0 ? 'L' : 'T';
        }
        const cell = grid.get(a).get(b);
        if (result === 'W') cell.wins++;
        else if (result === 'L') cell.losses++;
        else cell.ties++;
      }
    }
  }
  return grid;
}

/**
 * Each team's average and weekly swing (population standard deviation) over
 * its regular-season games in weeks 1..throughWeek. Teams with fewer than two
 * games are left out.
 * @returns {Array<{teamId: string|number, name: string, avg: number, swing: number}>}
 */
export function consistencyOf(matchupsByWeek, throughWeek) {
  return recordsThrough(matchupsByWeek, throughWeek)
    .filter(r => r.results.length >= 2)
    .map(r => {
      const points = r.results.map(x => x.points);
      const avg = points.reduce((s, p) => s + p, 0) / points.length;
      const swing = Math.sqrt(points.reduce((s, p) => s + (p - avg) ** 2, 0) / points.length);
      return { teamId: r.teamId, name: r.name, avg, swing };
    });
}
